import type { HazardsConfig } from '../config/hazards.config';

/**
 * Kinematics of the excavator arm (VEH_EXCAVATOR_A rig) in its vertical arm plane:
 * `forward` along the arm direction, `up` vertical. Joint angles are rotations about the
 * pivots' x axes relative to the rest pose, positive = downwards (the boom/stick/bucket
 * of the presenter use the same convention).
 */
export type ExcavatorRig = HazardsConfig['excavator']['rig'];

export interface ArmAngles {
  boom: number;
  stick: number;
  bucket: number;
}

export interface PlanePoint {
  forward: number;
  up: number;
}

const EPSILON = 1e-4;

/** Rotates a plane vector by a joint angle (positive = downwards, i.e. clockwise in forward/up). */
function rotate(forward: number, up: number, angle: number, out: PlanePoint): PlanePoint {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  out.forward = forward * c + up * s;
  out.up = -forward * s + up * c;
  return out;
}

/**
 * Joint angles that put the bucket pivot at (forward, up) relative to the boom pivot, with the
 * bucket turned to `curl` (its absolute rotation, positive = curled towards the machine).
 * Elbow up like a real excavator; unreachable targets are approached as far as possible.
 */
export function solveArm(rig: ExcavatorRig, forward: number, up: number, curl: number, out: ArmAngles): ArmAngles {
  const boomLength = Math.hypot(rig.stickPivot.forward, rig.stickPivot.up);
  const stickLength = Math.hypot(rig.bucketPivot.forward, rig.bucketPivot.up);
  const boomRest = Math.atan2(rig.stickPivot.up, rig.stickPivot.forward);
  const stickRest = Math.atan2(rig.bucketPivot.up, rig.bucketPivot.forward);
  const distance = Math.min(Math.max(Math.hypot(forward, up), Math.abs(boomLength - stickLength) + EPSILON), boomLength + stickLength - EPSILON);
  const cosInner = (boomLength * boomLength + distance * distance - stickLength * stickLength) / (2 * boomLength * distance);
  const boomElevation = Math.atan2(up, forward) + Math.acos(Math.min(1, Math.max(-1, cosInner)));
  const jointForward = boomLength * Math.cos(boomElevation);
  const jointUp = boomLength * Math.sin(boomElevation);
  const stickDirection = Math.atan2(up - jointUp, forward - jointForward);
  out.boom = boomRest - boomElevation;
  out.stick = stickRest - out.boom - stickDirection;
  out.bucket = curl - out.boom - out.stick;
  return out;
}

const scratch: PlanePoint = { forward: 0, up: 0 };
const pivotScratch: PlanePoint = { forward: 0, up: 0 };

/** Bucket pivot relative to the boom pivot for the given joint angles. */
export function bucketPivotOf(rig: ExcavatorRig, angles: ArmAngles, out: PlanePoint): PlanePoint {
  rotate(rig.stickPivot.forward, rig.stickPivot.up, angles.boom, out);
  const jointForward = out.forward;
  const jointUp = out.up;
  rotate(rig.bucketPivot.forward, rig.bucketPivot.up, angles.boom + angles.stick, out);
  out.forward += jointForward;
  out.up += jointUp;
  return out;
}

/** Bounds of the bucket outline in the arm plane, relative to the boom pivot. */
export interface PlaneBounds {
  minForward: number;
  maxForward: number;
  minUp: number;
  maxUp: number;
}

export function bucketBoundsOf(rig: ExcavatorRig, angles: ArmAngles, out: PlaneBounds): PlaneBounds {
  const pivot = bucketPivotOf(rig, angles, pivotScratch);
  const curl = angles.boom + angles.stick + angles.bucket;
  const { back, front, bottom, top } = rig.bucket;
  out.minForward = out.minUp = Infinity;
  out.maxForward = out.maxUp = -Infinity;
  for (let corner = 0; corner < 4; corner++) {
    rotate(corner < 2 ? back : front, corner % 2 === 0 ? bottom : top, curl, scratch);
    out.minForward = Math.min(out.minForward, pivot.forward + scratch.forward);
    out.maxForward = Math.max(out.maxForward, pivot.forward + scratch.forward);
    out.minUp = Math.min(out.minUp, pivot.up + scratch.up);
    out.maxUp = Math.max(out.maxUp, pivot.up + scratch.up);
  }
  return out;
}

/**
 * Arm direction (slew, radians from +z towards +x) and reach (forward distance of the bucket
 * pivot from the slew centre) that put the bucket over a point – the arm plane runs `side` to
 * the right of the slew centre.
 */
export function aimAt(rig: ExcavatorRig, dx: number, dz: number): { readonly slew: number; readonly reach: number } {
  const distance = Math.hypot(dx, dz);
  const side = rig.boomPivot.side;
  const reach = Math.sqrt(Math.max(distance * distance - side * side, 0));
  return { slew: Math.atan2(dx, dz) - Math.atan2(side, reach), reach };
}
